import { Injectable } from '@angular/core';
import { JobAlert } from '@models/profile/job-alert';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import {
  CreateJobAlert,
  CreateJobAlertSuccess,
  DeleteJobAlert,
  DeleteJobAlertSuccess,
  GetJobAlerts,
  GetJobAlertsSuccess,
  PatchJobAlert,
  PatchJobAlertSuccess,
} from '@store/job-alerts/job-alerts.actions';
import { JobAlertWebservice } from '@webservices/profile-api/job-alert.webservice';
import { switchMap } from 'rxjs/operators';

export interface JobAlertStateModel {
  jobAlerts: JobAlert[];
  loading: boolean;
}

const defaultState: JobAlertStateModel = {
  jobAlerts: [],
  loading: false,
};

@State<JobAlertStateModel>({
  name: 'jobAlerts',
  defaults: defaultState,
})
@Injectable()
export class JobAlertsState {
  @Selector()
  static jobAlerts(state: JobAlertStateModel): JobAlert[] {
    return state.jobAlerts;
  }

  constructor(private readonly jobAlertWebservice: JobAlertWebservice) {}

  @Action(GetJobAlerts)
  getJobAlerts(ctx: StateContext<JobAlertStateModel>, { profileId, force }: GetJobAlerts): any {
    if ((ctx.getState().jobAlerts.length === 0 || force) && !ctx.getState().loading) {
      ctx.patchState({ loading: true });
      return this.jobAlertWebservice
        .getBy(profileId)
        .pipe(switchMap((jobAlertResponse) => ctx.dispatch(new GetJobAlertsSuccess(jobAlertResponse))));
    }
  }

  @Action(GetJobAlertsSuccess)
  getJobAlertsSuccess(ctx: StateContext<JobAlertStateModel>, { jobAlerts }: GetJobAlertsSuccess): any {
    return ctx.patchState({ jobAlerts, loading: false });
  }

  @Action(CreateJobAlert)
  createJobAlert(ctx: StateContext<JobAlertStateModel>, { profileId, jobAlert }: CreateJobAlert): any {
    return this.jobAlertWebservice
      .create(profileId, jobAlert)
      .pipe(switchMap((jobAlertResponse) => ctx.dispatch(new CreateJobAlertSuccess(jobAlertResponse))));
  }

  @Action(CreateJobAlertSuccess)
  createJobAlertSuccess(ctx: StateContext<JobAlertStateModel>, { jobAlert }: CreateJobAlertSuccess): any {
    return ctx.setState(patch({ jobAlerts: append([jobAlert]) }));
  }

  @Action(PatchJobAlert)
  patchJobAlert(ctx: StateContext<JobAlertStateModel>, { profileId, jobAlert }: PatchJobAlert): any {
    return this.jobAlertWebservice
      .patch(profileId, jobAlert)
      .pipe(switchMap((jobAlertResponse) => ctx.dispatch(new PatchJobAlertSuccess(jobAlertResponse))));
  }

  @Action(PatchJobAlertSuccess)
  patchJobAlertSuccess(ctx: StateContext<JobAlertStateModel>, { jobAlert }: PatchJobAlertSuccess): any {
    return ctx.setState(
      patch({
        jobAlerts: updateItem<JobAlert>((jobAlertItem) => jobAlertItem.id === jobAlert.id, jobAlert),
      })
    );
  }

  @Action(DeleteJobAlert)
  deleteJobAlert(ctx: StateContext<JobAlertStateModel>, { profileId, jobAlertId }: DeleteJobAlert): any {
    return this.jobAlertWebservice
      .delete(profileId, jobAlertId)
      .pipe(switchMap(() => ctx.dispatch(new DeleteJobAlertSuccess(jobAlertId))));
  }

  @Action(DeleteJobAlertSuccess)
  deleteJobAlertSuccess(ctx: StateContext<JobAlertStateModel>, { jobAlertId }: DeleteJobAlertSuccess): any {
    return ctx.setState(
      patch({
        jobAlerts: removeItem<JobAlert>((jobAlertItem) => jobAlertItem.id === jobAlertId),
      })
    );
  }
}
